/**
 * Rax Cost Display Component
 *
 * Shows cost information inline after agent responses.
 * "This turn: $0.027 · 2 LLM calls · 1 gmail search [details]"
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxTheme } from "../styles/rax-theme.js";

export interface TurnCostData {
  totalUsd: number;
  llmCalls: number;
  toolCalls: number;
  skillAccesses: { skill: string; count: number }[];
  durationMs: number;
  details?: TurnCostDetail[];
}

export interface TurnCostDetail {
  type: "llm" | "tool" | "skill";
  name: string;
  costUsd: number;
  tokens?: { input: number; output: number };
  durationMs?: number;
}

@customElement("rax-cost-display")
export class RaxCostDisplay extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
        font-size: var(--font-xs);
        color: var(--text-secondary);
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        border: 1px solid var(--border-color);
        margin-top: var(--space-2);
      }
      
      .cost-summary {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-wrap: wrap;
      }
      
      .cost-icon {
        font-size: var(--font-sm);
        color: var(--rax-yellow);
      }
      
      .cost-total {
        font-weight: 600;
        color: var(--rax-yellow);
        font-family: monospace;
      }
      
      .cost-separator {
        color: var(--text-tertiary);
      }
      
      .cost-detail {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }
      
      .details-toggle {
        background: none;
        border: none;
        color: var(--rax-yellow);
        cursor: pointer;
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-sm);
        font-size: 11px;
        transition: all var(--transition-fast);
      }
      
      .details-toggle:hover {
        background: var(--accent-glow);
      }
      
      .details-panel {
        margin-top: var(--space-2);
        padding-top: var(--space-2);
        border-top: 1px solid var(--border-color);
      }
      
      .detail-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-1) 0;
        font-size: 11px;
      }
      
      .detail-name {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .detail-type {
        padding: 1px 6px;
        border-radius: var(--radius-sm);
        font-size: 10px;
        text-transform: uppercase;
        font-weight: 600;
      }
      
      .detail-type.llm {
        background: rgba(59, 130, 246, 0.2);
        color: var(--info);
      }
      
      .detail-type.tool {
        background: rgba(139, 92, 246, 0.2);
        color: #a78bfa;
      }
      
      .detail-type.skill {
        background: rgba(16, 185, 129, 0.2);
        color: var(--success);
      }
      
      .detail-cost {
        font-family: monospace;
        color: var(--text-primary);
      }
      
      .detail-tokens {
        color: var(--text-tertiary);
        font-size: 10px;
      }
    `,
  ];

  @property({ type: Object })
  data: TurnCostData | null = null;

  @state()
  private showDetails = false;

  override render() {
    if (!this.data)
      return html`
        
      `;

    const { totalUsd, llmCalls, toolCalls, skillAccesses, details } = this.data;

    return html`
      <div class="cost-summary">
        <span class="cost-icon">⚡</span>
        <span class="cost-detail">
          <span>This turn:</span>
          <span class="cost-total">${this.formatCost(totalUsd)}</span>
        </span>
        <span class="cost-separator">·</span>
        <span class="cost-detail">${llmCalls} LLM call${llmCalls !== 1 ? "s" : ""}</span>
        ${
          toolCalls > 0
            ? html`
          <span class="cost-separator">·</span>
          <span class="cost-detail">${toolCalls} tool${toolCalls !== 1 ? "s" : ""}</span>
        `
            : ""
        }
        ${
          skillAccesses.length > 0
            ? html`
          <span class="cost-separator">·</span>
          <span class="cost-detail">
            ${skillAccesses.map((s) => `${s.count} ${s.skill}`).join(", ")}
          </span>
        `
            : ""
        }
        ${
          details && details.length > 0
            ? html`
          <button class="details-toggle" @click=${this.toggleDetails}>
            ${this.showDetails ? "hide" : "details"}
          </button>
        `
            : ""
        }
      </div>
      ${this.showDetails && details ? this.renderDetails(details) : ""}
    `;
  }

  private renderDetails(details: TurnCostDetail[]) {
    return html`
      <div class="details-panel">
        ${details.map(
          (d) => html`
          <div class="detail-row">
            <div class="detail-name">
              <span class="detail-type ${d.type}">${d.type}</span>
              <span>${d.name}</span>
              ${
                d.tokens
                  ? html`
                <span class="detail-tokens">(${d.tokens.input} in / ${d.tokens.output} out)</span>
              `
                  : ""
              }
            </div>
            <span class="detail-cost">${this.formatCost(d.costUsd)}</span>
          </div>
        `,
        )}
      </div>
    `;
  }

  private toggleDetails() {
    this.showDetails = !this.showDetails;
  }

  private formatCost(usd: number): string {
    if (usd < 0.001) {
      return `$${usd.toFixed(4)}`;
    } else if (usd < 0.01) {
      return `$${usd.toFixed(3)}`;
    } else {
      return `$${usd.toFixed(2)}`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-cost-display": RaxCostDisplay;
  }
}
